import * as React from 'react';
import {
  deleteTask,
  getTask,
  transitionTask,
  updateTask,
  type Task,
  type TaskColumn,
  type TaskStatus,
} from '../lib/tasks-api.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  formatDueDate,
  fromDateTimeLocalValue,
  taskOriginKey,
  toDateTimeLocalValue,
} from '../lib/task-board.js';
import { useT } from '../lib/i18n.js';
import { DateTimeField } from './date-field.js';

export interface TaskDrawerProps {
  readonly taskId: string;
  readonly columns: readonly TaskColumn[];
  readonly onClose: () => void;
  readonly onChanged: (task: Task) => void;
  readonly onDeleted: (taskId: string) => void;
}

/**
 * Detail karty úkolu (krok CRM2, bod 1) — vzorem `DealDrawer` z crm-web:
 * hlavička, obsah, přesun mezi sloupci, vazby a ODKAZ NA ZDROJ, u kterého
 * automaticky vznikl úkol.
 *
 * Přesun jde přes `allowedTransitions` z API — žádný „tahni kam chceš“
 * vedle si necháváme (stejně jako u poptávek).
 */
export function TaskDrawer({
  taskId,
  columns,
  onClose,
  onChanged,
  onDeleted,
}: TaskDrawerProps): React.JSX.Element {
  const t = useT();
  const [task, setTask] = React.useState<Task | null>(null);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [dueAt, setDueAt] = React.useState('');
  const [ownerUserId, setOwnerUserId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const applyTask = React.useCallback((value: Task) => {
    setTask(value);
    setTitle(value.title);
    setDescription(value.description ?? '');
    setDueAt(toDateTimeLocalValue(value.dueAt));
    setOwnerUserId(value.ownerUserId ?? '');
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null);
    void getTask(taskId)
      .then((value) => {
        if (!cancelled) applyTask(value);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTask(null);
        setError(
          e instanceof DomainApiError ? e.message : t('webAdmin.tasks.drawer.loadFailed'),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, applyTask, t]);

  async function save(): Promise<void> {
    if (task === null) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateTask(task.id, {
        title: title.trim(),
        description: description.trim(),
        dueAt: fromDateTimeLocalValue(dueAt),
        ownerUserId: ownerUserId.trim() === '' ? null : ownerUserId.trim(),
      });
      applyTask(updated);
      onChanged(updated);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.drawer.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function move(toStatus: TaskStatus): Promise<void> {
    if (task === null) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await transitionTask(task.id, toStatus);
      applyTask(updated);
      onChanged(updated);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.drawer.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (task === null) return;
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.tasks.drawer.deleteConfirm'))) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTask(task.id);
      onDeleted(task.id);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.tasks.drawer.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  const columnLabel = new Map(columns.map((column) => [column.key, column.label]));
  const origin = taskOriginKey(task?.originKind);

  return (
    <aside className="rh-demand-drawer" data-testid="task-drawer" aria-label={t('webAdmin.tasks.drawer.heading')}>
      <header className="rh-demand-drawer__header">
        <h2 className="rh-kanban-card__title">{task?.title ?? t('webAdmin.tasks.drawer.heading')}</h2>
        <button type="button" className="rh-btn" onClick={onClose} data-testid="task-drawer-close">
          {t('webAdmin.tasks.drawer.close')}
        </button>
      </header>

      {error !== null && (
        <p role="alert" className="rh-demand-drawer__error" data-testid="task-drawer-error">
          {error}
        </p>
      )}

      {task !== null && (
        <>
          <section className="rh-demand-drawer__section">
            <h3>{t('webAdmin.tasks.form.titleLabel')}</h3>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="task-drawer-title"
              aria-label={t('webAdmin.tasks.form.titleLabel')}
            />
            <h3>{t('webAdmin.tasks.form.descriptionLabel')}</h3>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="task-drawer-description"
              aria-label={t('webAdmin.tasks.form.descriptionLabel')}
            />
            <h3>{t('webAdmin.tasks.drawer.due')}</h3>
            <DateTimeField
              value={dueAt}
              onValueChange={setDueAt}
              testId="task-drawer-due"
              ariaLabel={t('webAdmin.tasks.drawer.due')}
            />
            <h3>{t('webAdmin.tasks.drawer.owner')}</h3>
            <input
              value={ownerUserId}
              onChange={(e) => setOwnerUserId(e.target.value)}
              data-testid="task-drawer-owner"
              aria-label={t('webAdmin.tasks.drawer.owner')}
              placeholder={t('webAdmin.tasks.noOwner')}
            />
            <button
              type="button"
              className="rh-btn rh-btn--primary"
              disabled={busy || title.trim() === ''}
              onClick={() => void save()}
              data-testid="task-drawer-save"
            >
              {t('webAdmin.tasks.drawer.save')}
            </button>
          </section>

          <section className="rh-demand-drawer__section">
            <h3>{t('webAdmin.tasks.drawer.statusMove')}</h3>
            {task.allowedTransitions.length === 0 ? (
              <p data-testid="task-drawer-no-moves">{t('webAdmin.tasks.noMoveAvailable')}</p>
            ) : (
              <div className="rh-demand-drawer__moves" data-testid="task-drawer-moves">
                {task.allowedTransitions.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className="rh-btn"
                    disabled={busy || !task.canUpdate}
                    data-testid={`task-move-${key}`}
                    onClick={() => void move(key)}
                  >
                    {columnLabel.get(key) ?? key}
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="rh-demand-drawer__section">
            <h3>{t('webAdmin.tasks.drawer.links')}</h3>
            <dl className="rh-demand-drawer__grid">
              <dt>{t('webAdmin.tasks.drawer.case')}</dt>
              <dd data-testid="task-drawer-case">{task.caseId ?? '—'}</dd>
              <dt>{t('webAdmin.tasks.drawer.party')}</dt>
              <dd data-testid="task-drawer-party">{task.partyId ?? '—'}</dd>
              <dt>{t('webAdmin.tasks.drawer.demand')}</dt>
              <dd data-testid="task-drawer-demand">{task.demandId ?? '—'}</dd>
            </dl>
          </section>

          <section className="rh-demand-drawer__section">
            <h3>{t('webAdmin.tasks.originHeading')}</h3>
            {origin === null ? (
              <p data-testid="task-drawer-no-origin">{t('webAdmin.tasks.originNone')}</p>
            ) : (
              <dl className="rh-demand-drawer__grid" data-testid="task-drawer-origin">
                <dt>{t('webAdmin.tasks.originHeading')}</dt>
                <dd data-testid="task-drawer-origin-kind">
                  {t(`webAdmin.tasks.origin.${origin}` as 'webAdmin.tasks.origin.case_control')}
                </dd>
                <dt>{t('webAdmin.tasks.drawer.sourceRef')}</dt>
                <dd data-testid="task-drawer-origin-ref">{task.originRef ?? '—'}</dd>
                <dt>{t('webAdmin.tasks.drawer.description')}</dt>
                <dd data-testid="task-drawer-origin-label">{task.originLabel ?? '—'}</dd>
                <dt>{t('webAdmin.tasks.drawer.due')}</dt>
                <dd>{formatDueDate(task.dueAt) || '—'}</dd>
              </dl>
            )}
          </section>

          {task.canDelete && (
            <section className="rh-demand-drawer__section">
              <button
                type="button"
                className="rh-btn"
                disabled={busy}
                onClick={() => void remove()}
                data-testid="task-drawer-delete"
              >
                {t('webAdmin.tasks.drawer.delete')}
              </button>
            </section>
          )}
        </>
      )}
    </aside>
  );
}
